const SCROLL_EDGE_THRESHOLD = 4;

class AweMultipleCollectionsComponent extends HTMLElement {
  /** @type {HTMLElement|null} */
  _tabsWrap = null;
  /** @type {HTMLElement|null} */
  _tabsScroller = null;
  /** @type {HTMLButtonElement|null} */
  _prevArrow = null;
  /** @type {HTMLButtonElement|null} */
  _nextArrow = null;
  /** @type {ResizeObserver|null} */
  _resizeObserver = null;
  /** @type {number|null} */
  _rafId = null;

  connectedCallback() {
    this._tabs = Array.from(this.querySelectorAll('.awe-mc__tab'));
    this._panels = Array.from(this.querySelectorAll('.awe-mc__panel'));

    this._tabs.forEach((tab) => {
      tab.addEventListener('click', () => this._selectTab(tab));
    });

    this._setupTabArrows();
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._tabsScroller?.removeEventListener('scroll', this._onTabsScroll);
    if (this._rafId !== null) cancelAnimationFrame(this._rafId);
  }

  _selectTab(activeTab) {
    const targetIndex = parseInt(activeTab.dataset.panel, 10);

    this._tabs.forEach((tab, i) => {
      const selected = i === targetIndex;
      tab.setAttribute('aria-selected', String(selected));
    });

    this._panels.forEach((panel, i) => {
      if (i === targetIndex) {
        panel.classList.remove('awe-mc__panel--hidden');
      } else {
        panel.classList.add('awe-mc__panel--hidden');
      }
    });

    activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  _setupTabArrows() {
    this._tabsWrap = this.querySelector('.awe-mc__tabs-wrap');
    this._tabsScroller = this.querySelector('.awe-mc__tabs');
    this._prevArrow = this.querySelector('.awe-mc__tabs-arrow--prev');
    this._nextArrow = this.querySelector('.awe-mc__tabs-arrow--next');

    if (!this._tabsWrap || !this._tabsScroller || !this._prevArrow || !this._nextArrow) return;

    this._prevArrow.addEventListener('click', () => this._scrollTabsBy(-1));
    this._nextArrow.addEventListener('click', () => this._scrollTabsBy(1));
    this._tabsScroller.addEventListener('scroll', this._onTabsScroll, { passive: true });

    this._resizeObserver = new ResizeObserver(this._updateArrowVisibility);
    this._resizeObserver.observe(this._tabsScroller);

    this._updateArrowVisibility();
  }

  _onTabsScroll = () => {
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._updateArrowState();
    });
  };

  _updateArrowVisibility = () => {
    if (!this._tabsScroller || !this._tabsWrap) return;
    const needsArrows = this._tabsScroller.scrollWidth > this._tabsScroller.clientWidth + 1;

    this._tabsWrap.classList.toggle('awe-mc__tabs-wrap--scrollable', needsArrows);
    if (this._prevArrow) this._prevArrow.hidden = !needsArrows;
    if (this._nextArrow) this._nextArrow.hidden = !needsArrows;

    if (needsArrows) this._updateArrowState();
  };

  _updateArrowState = () => {
    if (!this._tabsScroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = this._tabsScroller;
    const atStart = scrollLeft <= SCROLL_EDGE_THRESHOLD;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - SCROLL_EDGE_THRESHOLD;

    if (this._prevArrow) this._prevArrow.disabled = atStart;
    if (this._nextArrow) this._nextArrow.disabled = atEnd;
  };

  _scrollTabsBy(direction) {
    if (!this._tabsScroller) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const distance = this._tabsScroller.clientWidth * 0.8 * direction;

    this._tabsScroller.scrollBy({ left: distance, behavior: reducedMotion ? 'instant' : 'smooth' });
  }
}

customElements.define('awe-multiple-collections-component', AweMultipleCollectionsComponent);
